import { PaginationQueryVariables, PartialQueryVariables } from '@tableau/data-catalog-client-components';
import { ListResult } from '../../ts/fetchers/ListResult';
import { BaseParamsBuilderResourceQuery } from '../../ts/resources/ResourceQuery';
import { GraphQLSliceableFactory, ItemWithId } from './GraphQLSliceable';

/**
 * This is a base class for all GraphQL resource queries. We use a base class so the fetching logic
 * can easily distinguish between traditional and GraphQL resources.
 */
export abstract class BaseGraphQLResourceQuery<TQueryVariables extends PaginationQueryVariables, TResultItem extends ItemWithId>
extends BaseParamsBuilderResourceQuery<TResultItem> {

  /**
   * @param {GraphQLSliceableFactory<TQueryVariables, TResultItem>} sliceableFactory
   *    This factory wraps GraphQL query variables and returns a sliceable that can be used to make
   *    a paginated GraphQL query for those query variables.
   */
  protected constructor(protected sliceableFactory: GraphQLSliceableFactory<TQueryVariables, TResultItem>) {
    super();
  }

  protected abstract getPartialQueryVariables(): PartialQueryVariables<TQueryVariables>;

  public getSlice(start: number, size: number): ng.IPromise<ListResult<TResultItem>> {
    const queryVariables = this.getPartialQueryVariables();
    const sliceable = this.sliceableFactory(queryVariables);
    return sliceable.getSlice(start, size);
  }

  public getCount(): ng.IPromise<number> {
    // There are certainly cleverer ways to get this data than by making another query.
    // But that would be premature optimization.
    const queryVariables = this.getPartialQueryVariables();
    const sliceable = this.sliceableFactory(queryVariables);
    return sliceable.getSlice(0, 0)
      .then(listResult => listResult.total)
      .catch((reason: any) => {
        // Ignore errors because the service can be down.
        // We don't need to report to the user when getting counts fails, that's annoying.
        return 0;
      });
  }

  public getFirst(): ng.IPromise<TResultItem> {
    throw new Error('getFirst is not used for GraphQL resource queries. Implement it later if we need to!');
  }
}
